import { useState } from "react";
import toast from "react-hot-toast";
import { FileStack, Plus, AlertTriangle } from "lucide-react";
import { useProcedures, useUpdateProcedure, PROCEDURE_STATUS_LABEL, ProcedureStatus, isProcedureOverdue } from "@/api/procedures";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import CreateProcedureModal from "@/pages/procedures/CreateProcedureModal";

const STATUS_STYLE: Record<ProcedureStatus, string> = {
  PENDIENTE: "bg-gray-100 text-gray-600",
  EN_PROCESO: "bg-blue-100 text-blue-700",
  EN_REVISION: "bg-purple-100 text-purple-700",
  APROBADO: "bg-teal-100 text-teal-700",
  FINALIZADO: "bg-green-100 text-green-700",
  CANCELADO: "bg-gray-100 text-gray-400",
};

const STATUS_OPTIONS = Object.keys(PROCEDURE_STATUS_LABEL) as ProcedureStatus[];

export default function ProceduresSection({ clientId }: { clientId: string }) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { data, isLoading } = useProcedures({ clientId, pageSize: 50 });
  const updateProcedure = useUpdateProcedure();
  const [showCreate, setShowCreate] = useState(false);

  const handleStatusChange = async (id: string, status: ProcedureStatus) => {
    try {
      await updateProcedure.mutateAsync({ id, status });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Trámites</h2>
        {hasPermission("procedures.create") && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
            onClick={() => setShowCreate(true)}
          >
            <Plus size={14} /> Nuevo trámite
          </button>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {isLoading && <p className="text-sm text-gray-400">Cargando trámites...</p>}
        {!isLoading && (!data?.data || data.data.length === 0) && (
          <p className="text-sm text-gray-400">Este cliente aún no tiene trámites registrados.</p>
        )}
        {data?.data.map((procedure) => (
          <div key={procedure.id} className="flex items-center justify-between rounded-md border border-gray-100 p-3">
            <div className="flex items-center gap-3 overflow-hidden">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
                <FileStack size={15} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{procedure.title}</p>
                <p className="text-xs text-gray-500">
                  {procedure.entityName || "Sin entidad"}
                  {procedure.dueDate && (
                    <span className={isProcedureOverdue(procedure) ? "ml-1 inline-flex items-center gap-1 text-red-600" : "ml-1"}>
                      {isProcedureOverdue(procedure) && <AlertTriangle size={10} className="inline" />} · Vence {procedure.dueDate}
                    </span>
                  )}
                </p>
              </div>
            </div>
            {hasPermission("procedures.update") ? (
              <select
                className={`shrink-0 rounded-full border-0 px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[procedure.status]}`}
                value={procedure.status}
                onChange={(e) => handleStatusChange(procedure.id, e.target.value as ProcedureStatus)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {PROCEDURE_STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            ) : (
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[procedure.status]}`}>
                {PROCEDURE_STATUS_LABEL[procedure.status]}
              </span>
            )}
          </div>
        ))}
      </div>

      {showCreate && <CreateProcedureModal clientId={clientId} onClose={() => setShowCreate(false)} />}
    </div>
  );
}
